import {
  Provider as RadixToastProvider,
  Viewport as RadixToastViewport,
  Root as RadixToastRoot,
  Title as RadixToastTitle,
  Description as RadixToastDescription,
} from "@radix-ui/react-toast";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { CircleCheck, CircleAlert, TriangleAlert, X } from "lucide-react";

export type ToastVariant = "success" | "error" | "info";

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextValue {
  publish: (toast: Omit<ToastItem, "id">) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  success: "border-l-4 border-l-emerald-500 bg-white",
  error: "border-l-4 border-l-red-600 bg-white",
  info: "border-l-4 border-l-blue-500 bg-white",
};

const VARIANT_ICON: Record<ToastVariant, typeof CircleCheck> = {
  success: CircleCheck,
  error: TriangleAlert,
  info: CircleAlert,
};

const VARIANT_ICON_COLOR: Record<ToastVariant, string> = {
  success: "text-emerald-600",
  error: "text-red-600",
  info: "text-blue-600",
};

function nextId(): string {
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const publish = useCallback((toast: Omit<ToastItem, "id">) => {
    setToasts((prev) => [...prev, { id: nextId(), ...toast }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ publish }), [publish]);

  return (
    <ToastContext.Provider value={value}>
      <RadixToastProvider swipeDirection="right">
        {children}
        {toasts.map((toast) => {
          const Icon = VARIANT_ICON[toast.variant];
          return (
            <RadixToastRoot
              key={toast.id}
              duration={toast.duration ?? 4000}
              onOpenChange={(open) => {
                if (!open) dismiss(toast.id);
              }}
              className={`pointer-events-auto flex w-full max-w-sm animate-fade-in items-start gap-3 rounded-2xl border border-neutral-100 p-4 shadow-soft ${VARIANT_CLASSES[toast.variant]}`}
            >
              <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${VARIANT_ICON_COLOR[toast.variant]}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <RadixToastTitle className="text-sm font-semibold text-neutral-900">
                  {toast.title}
                </RadixToastTitle>
                {toast.description && (
                  <RadixToastDescription className="mt-0.5 text-xs text-neutral-600">
                    {toast.description}
                  </RadixToastDescription>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label="Dismiss notification"
                className="flex-shrink-0 rounded p-0.5 text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </RadixToastRoot>
          );
        })}
        <RadixToastViewport className="fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2 outline-none" />
      </RadixToastProvider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside a <ToastProvider>");
  }
  return ctx;
}
